import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { fl } from "@/lib/finI18n";
import { tkuOf } from "@/lib/kop";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { AlertTriangle, BadgeCheck } from "lucide-react";

const WARN = "rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-3";

export function TaxIdCheck({ npwp, nik, lang, testid }) {
  const [iss, setIss] = useState(null);
  useEffect(() => {
    setIss(null);
    if (!String(npwp || "").trim() && !String(nik || "").trim()) return undefined;
    const h = setTimeout(() => {
      api.get(`/tax-ids/check?npwp=${encodeURIComponent(npwp || "")}&nik=${encodeURIComponent(nik || "")}`).then(({ data }) => setIss(data.issues)).catch(() => {});
    }, 400);
    return () => clearTimeout(h);
  }, [npwp, nik]);
  if (!iss) return null;
  if (!iss.length) return <p className="text-[11px] text-emerald-700 dark:text-emerald-400 flex items-center gap-1" data-testid={`${testid}-ok`}><BadgeCheck className="h-3.5 w-3.5" />{fl("tiFormatOk", lang)}</p>;
  return (
    <div className="text-[11px] text-amber-700 dark:text-amber-300 space-y-0.5" data-testid={`${testid}-issues`}>
      {iss.map((c) => <p key={c} className="flex items-center gap-1"><AlertTriangle className="h-3 w-3 shrink-0" />{fl(`tiIss_${c}`, lang)}</p>)}
    </div>
  );
}

export function TaxIdFields({ f, setF, testid, lang }) {
  return (
    <div className="space-y-1">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Input placeholder="NPWP" value={f.npwp} onChange={(e) => setF({ ...f, npwp: e.target.value })} className="h-9 num" data-testid={`${testid}-npwp`} />
        <Input placeholder="NIK" value={f.nik} onChange={(e) => setF({ ...f, nik: e.target.value })} className="h-9 num" data-testid={`${testid}-nik`} />
        <Input placeholder={tkuOf(f.npwp, f.nik) || "ID TKU"} value={f.id_tku} onChange={(e) => setF({ ...f, id_tku: e.target.value })} className="h-9 num" data-testid={`${testid}-tku`} />
      </div>
      <TaxIdCheck npwp={f.npwp} nik={f.nik} lang={lang} testid={`${testid}-check`} />
    </div>
  );
}

// Reminder in the transaction form: a taxable transaction whose counterparty has no NPWP / NIK in the ledger master.
export function TaxIdReminder({ clientId, name, lang }) {
  const [info, setInfo] = useState(null);
  const [f, setF] = useState({ npwp: "", nik: "", id_tku: "" });
  const [saving, setSaving] = useState(false);
  const nm = (name || "").trim();
  useEffect(() => {
    setInfo(null);
    if (!nm || !clientId) return undefined;
    let off = false;
    const h = setTimeout(() => {
      api.get(`/tax-ids/party?client_id=${clientId}&name=${encodeURIComponent(nm)}`).then(({ data }) => { if (!off) setInfo(data); }).catch(() => {});
    }, 400);
    return () => { off = true; clearTimeout(h); };
  }, [clientId, nm]);

  const save = async () => {
    if (!f.npwp.trim() && !f.nik.trim()) { toast.error(fl("taxIdNeedOne", lang)); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/tax-ids/party", { client_id: clientId, name: nm, ...f });
      setInfo({ ...info, ...data, ok: true });
      toast.success(fl("taxIdSaved", lang));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  if (!nm) return <div className={`${WARN} text-xs text-amber-800 dark:text-amber-300 flex gap-1.5`} data-testid="txn-taxid-noparty"><AlertTriangle className="h-4 w-4 shrink-0" />{fl("taxIdNoParty", lang)}</div>;
  if (!info) return null;
  if (info.ok) {
    return (
      <p className="text-[11px] text-emerald-700 dark:text-emerald-400 flex items-center gap-1" data-testid="txn-taxid-ok">
        <BadgeCheck className="h-3.5 w-3.5" />{nm} · {info.npwp ? `NPWP ${info.npwp}` : `NIK ${info.nik}`} · ID TKU {info.id_tku || "—"}
      </p>
    );
  }
  return (
    <div className={`${WARN} space-y-2`} data-testid="txn-taxid-warning">
      <p className="text-xs text-amber-800 dark:text-amber-300 flex gap-1.5"><AlertTriangle className="h-4 w-4 shrink-0" /><span>{fl("taxIdFormHint", lang)} <b>{nm}</b></span></p>
      <TaxIdFields f={f} setF={setF} testid="txn-taxid" lang={lang} />
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-[11px] text-muted-foreground">{fl("tkuAuto", lang)}</span>
        <Button type="button" size="sm" variant="outline" onClick={save} disabled={saving} data-testid="txn-taxid-save">{fl("saveToLedger", lang)}</Button>
      </div>
    </div>
  );
}
